/**
 * A1 notation: column labels, cell references, and ranges.
 *
 * Internally rows and columns are 0-based; A1 text is 1-based rows and
 * base-26-bijective letter columns (`A`..`XFD`). The syntactic reference shape
 * here matches the `ref` node of the formula AST, so the parser can reuse it.
 */

import { MAX_COLS, MAX_ROWS, type Addr, type Range } from './types.js';

/** A syntactic A1 reference. `row`/`col` are 0-based. */
export interface A1Ref {
  sheet?: string;
  absCol: boolean;
  col: number;
  absRow: boolean;
  row: number;
}

/** A syntactic A1 range. The sheet, when present, lives on the range. */
export interface A1Range {
  sheet?: string;
  from: A1Ref;
  to: A1Ref;
}

const A = 'A'.charCodeAt(0);

/** Matches `$A$1` style references with no sheet prefix. */
const REF_BODY = /^(\$?)([A-Za-z]{1,3})(\$?)([0-9]{1,10})$/;

/** Sheet names that need no quoting when printed. */
const BARE_SHEET_NAME = /^[A-Za-z_][A-Za-z0-9_]*$/;

/**
 * Converts a 0-based column index to its A1 label.
 * 0 -> `A`, 25 -> `Z`, 26 -> `AA`, 18277 -> `ZZZ`.
 */
export function colToLabel(col: number): string {
  if (!Number.isInteger(col) || col < 0 || col >= MAX_COLS) {
    throw new RangeError(`column index out of range: ${col}`);
  }
  let n = col;
  let label = '';
  for (;;) {
    label = String.fromCharCode(A + (n % 26)) + label;
    n = Math.floor(n / 26) - 1;
    if (n < 0) return label;
  }
}

/**
 * Converts an A1 column label to its 0-based index. Case-insensitive.
 * Throws `RangeError` when the label is malformed or beyond `ZZZ`.
 */
export function labelToCol(label: string): number {
  const n = tryLabelToCol(label);
  if (n === null) throw new RangeError(`invalid column label: ${JSON.stringify(label)}`);
  return n;
}

/** Non-throwing form of {@link labelToCol}. Returns `null` when invalid. */
export function tryLabelToCol(label: string): number | null {
  if (label.length < 1 || label.length > 3) return null;
  let n = 0;
  for (let i = 0; i < label.length; i++) {
    const code = label.charCodeAt(i);
    const upper = code >= 97 && code <= 122 ? code - 32 : code;
    if (upper < A || upper > A + 25) return null;
    n = n * 26 + (upper - A + 1);
  }
  const col = n - 1;
  return col < MAX_COLS ? col : null;
}

/** Parses a single A1 reference, optionally sheet-qualified. */
export function parseA1Ref(text: string): A1Ref | null {
  const split = splitSheetPrefix(text);
  if (split === null) return null;
  const body = parseRefBody(split.rest);
  if (body === null) return null;
  return split.sheet === undefined ? body : { ...body, sheet: split.sheet };
}

/** Prints a reference in canonical A1 form, quoting the sheet name if needed. */
export function printA1Ref(ref: A1Ref): string {
  const body = `${ref.absCol ? '$' : ''}${colToLabel(ref.col)}${ref.absRow ? '$' : ''}${ref.row + 1}`;
  return ref.sheet === undefined ? body : `${printSheetName(ref.sheet)}!${body}`;
}

/**
 * Parses an A1 range (`A1:B2`, `Sheet1!$A$1:$B$2`) or a single reference
 * (treated as a degenerate range). A sheet prefix on the right-hand reference
 * is accepted only when it matches the left-hand sheet.
 */
export function parseA1Range(text: string): A1Range | null {
  const split = splitSheetPrefix(text);
  if (split === null) return null;
  const parts = split.rest.split(':');
  if (parts.length > 2) return null;
  const left = parseRefBody(parts[0] ?? '');
  if (left === null) return null;
  if (parts.length === 1) {
    return split.sheet === undefined
      ? { from: left, to: left }
      : { sheet: split.sheet, from: left, to: left };
  }
  const rightText = parts[1] ?? '';
  const rightSplit = splitSheetPrefix(rightText);
  if (rightSplit === null) return null;
  if (rightSplit.sheet !== undefined && rightSplit.sheet !== split.sheet) return null;
  const right = parseRefBody(rightSplit.rest);
  if (right === null) return null;
  return split.sheet === undefined
    ? { from: left, to: right }
    : { sheet: split.sheet, from: left, to: right };
}

/** Prints a range in canonical A1 form. Single-cell ranges print as one ref. */
export function printA1Range(range: A1Range): string {
  const from = printA1Ref(stripSheet(range.from));
  const to = printA1Ref(stripSheet(range.to));
  const body = from === to ? from : `${from}:${to}`;
  return range.sheet === undefined ? body : `${printSheetName(range.sheet)}!${body}`;
}

/** Prints a resolved {@link Addr} as A1 text, without a sheet prefix. */
export function addrToA1(a: Addr): string {
  return printA1Ref({ absCol: false, col: a.col, absRow: false, row: a.row });
}

/** Prints a resolved {@link Range} as A1 text, without a sheet prefix. */
export function rangeToA1(r: Range): string {
  return printA1Range({
    from: { absCol: false, col: r.c0, absRow: false, row: r.r0 },
    to: { absCol: false, col: r.c1, absRow: false, row: r.r1 },
  });
}

/** Quotes a sheet name only when it contains characters outside `[A-Za-z0-9_]`. */
export function printSheetName(name: string): string {
  return BARE_SHEET_NAME.test(name) ? name : `'${name.replace(/'/g, "''")}'`;
}

function stripSheet(ref: A1Ref): A1Ref {
  return { absCol: ref.absCol, col: ref.col, absRow: ref.absRow, row: ref.row };
}

function parseRefBody(text: string): A1Ref | null {
  const m = REF_BODY.exec(text);
  if (m === null) return null;
  const col = tryLabelToCol(m[2] as string);
  if (col === null) return null;
  const row1 = Number(m[4] as string);
  if (!Number.isInteger(row1) || row1 < 1 || row1 > MAX_ROWS) return null;
  return {
    absCol: m[1] === '$',
    col,
    absRow: m[3] === '$',
    row: row1 - 1,
  };
}

/**
 * Splits an optional `Sheet!` or `'Sheet name'!` prefix from a reference.
 * Returns `null` when the prefix is malformed (for example an unterminated quote).
 */
function splitSheetPrefix(text: string): { sheet?: string; rest: string } | null {
  if (text.startsWith("'")) {
    let i = 1;
    let name = '';
    for (;;) {
      if (i >= text.length) return null;
      const ch = text[i] as string;
      if (ch === "'") {
        if (text[i + 1] === "'") {
          name += "'";
          i += 2;
          continue;
        }
        i += 1;
        break;
      }
      name += ch;
      i += 1;
    }
    if (text[i] !== '!' || name.length === 0) return null;
    return { sheet: name, rest: text.slice(i + 1) };
  }
  const bang = text.indexOf('!');
  if (bang === -1) return { rest: text };
  const name = text.slice(0, bang);
  if (!BARE_SHEET_NAME.test(name)) return null;
  return { sheet: name, rest: text.slice(bang + 1) };
}
